import { AlertPriority, AlertType } from '../../types/admin';

export type AlertRole = 'admin' | 'evaluador';
export type AlertAnchor = 'application' | 'evaluator' | 'assignment';

export interface AlertCatalogEntry {
  type: AlertType;
  label: string;
  description: string;
  visibleParaRol: AlertRole[];
  anchor: AlertAnchor;
  defaultPriority: AlertPriority;
}

// DEV NOTE (2026-06-30): All numeric constants here are PROTOTYPE PLACEHOLDERS intended to
// make demo data visible in the UI. Production values must come from contest configuration:
//
//   OVERLOAD_THRESHOLD  → Contest.maxAssignmentsPerEvaluator (per-contest, set by DINV).
//                         The Acta (EVA-010) explicitly requires this to be configurable.
//                         No fixed value has been agreed yet; DINV uses it operationally
//                         to decide who still has capacity. See also: EvalByEvaluatorRow.isOverloaded.
//
// *** UPDATED (2026-07-01): CUMULATIVE semantics — the quota is per-concurso and
//                         accumulates permanently. A slot is consumed when an evaluator
//                         is assigned (regardless of final status), and is NEVER freed,
//                         even after submitting (completed). Only declined and
//                         conflict_declared assignments do NOT consume a slot (no real
//                         work was done).
//                         Formula: disponible = maxAssignmentsPerEvaluator − totalUsed
//                         where totalUsed = activeLoad + completed
//                               activeLoad = pending + accepted_not_started + in_progress.
//                         isOverloaded fires when totalUsed >= maxAssignmentsPerEvaluator (>=, not >).
//                         NOTE: the EVALUATOR_OVERLOAD alert below uses activeAssignments.length
//                         (active-only), which is a separate concept (simultaneous workload alert)
//                         — it is NOT the same as the cumulative quota check above.
//
//   INVITATION_RESPONSE_DAYS / EVALUATOR_LOGIN_DAYS / EVALUATION_DELAYED_* → These could
//   also be per-contest parameters (Acta ADM-033), but are more likely to stay global.
//   Confirm with DINV before hardcoding in production.
//
//   SCORE_VARIANCE_THRESHOLD → Must match the rubric threshold configured per contest
//   (Acta EVA-050/051). Currently 1 point; DINV confirmed this for Avanza UC 2026 Línea A.
export const ALERT_CONFIG = {
  INVITATION_RESPONSE_DAYS: 3,        // days without response before INVITATION_NO_RESPONSE fires
  EVALUATOR_LOGIN_DAYS: 5,            // days without login before EVALUATOR_NO_LOGIN fires
  OVERLOAD_THRESHOLD: 6,              // cupo por evaluador del concurso (cumulative, see DEV NOTE above); production: Contest.maxAssignmentsPerEvaluator
  SCORE_VARIANCE_THRESHOLD: 1,        // max allowed score delta between evaluators

  // EVALUATION_DELAYED escalation:
  //   ≥ 11 days since accepted assignment without submitting → fires at MEDIUM priority
  //   ≥ 14 days                                             → escalates to HIGH priority
  EVALUATION_DELAYED_MEDIUM_DAYS: 11,
  EVALUATION_DELAYED_HIGH_DAYS: 14,

  DEADLINE_WARNING_DAYS: [7, 1],      // days before contest deadline to fire DEADLINE_APPROACHING
} as const;

// 8 canonical types — one-to-one with the spec (ADM-040/042)
export const ALERT_CATALOG: AlertCatalogEntry[] = [
  {
    type: AlertType.EVALUATION_SCORE_VARIANCE,
    label: 'Desviación de Puntaje',
    description: 'Dos evaluaciones completadas difieren más del umbral configurado.',
    visibleParaRol: ['admin'],
    anchor: 'application',
    defaultPriority: AlertPriority.HIGH,
  },
  {
    type: AlertType.DEADLINE_APPROACHING,
    label: 'Plazo Próximo a Vencer',
    description: 'El plazo de cierre del concurso está próximo a vencer.',
    visibleParaRol: ['admin'],
    anchor: 'application',
    defaultPriority: AlertPriority.MEDIUM, // escala a HIGH al acercarse — ver Fase 3
  },
  {
    type: AlertType.MANUAL,
    label: 'Alerta Manual',
    description: 'Creada directamente por el administrador.',
    visibleParaRol: ['admin'],
    anchor: 'application',
    defaultPriority: AlertPriority.MEDIUM,
  },
  {
    type: AlertType.INVITATION_NO_RESPONSE,
    label: 'Invitación Sin Respuesta',
    description: 'El evaluador no ha respondido a la invitación en el plazo definido.',
    visibleParaRol: ['admin'],
    anchor: 'assignment',
    defaultPriority: AlertPriority.MEDIUM,
  },
  {
    type: AlertType.INVITATION_REJECTED,
    label: 'Invitación Rechazada',
    description: 'El evaluador rechazó la invitación o declaró conflicto de interés.',
    visibleParaRol: ['admin'],
    anchor: 'assignment',
    defaultPriority: AlertPriority.MEDIUM,
  },
  {
    type: AlertType.EVALUATION_DELAYED,
    label: 'Evaluación Atrasada',
    description: 'La evaluación aceptada no ha sido enviada dentro del plazo.',
    visibleParaRol: ['admin'],
    anchor: 'assignment',
    // Priority is determined by days elapsed since the assignment was accepted:
    //   MEDIUM : ALERT_CONFIG.EVALUATION_DELAYED_MEDIUM_DAYS (11) ≤ days < 14
    //   HIGH   : ALERT_CONFIG.EVALUATION_DELAYED_HIGH_DAYS  (14) ≤ days
    defaultPriority: AlertPriority.MEDIUM,
  },
  {
    type: AlertType.EVALUATOR_NO_LOGIN,
    label: 'Evaluador Sin Actividad',
    // DEV NOTE (2026-06-26): "actividad" = cualquier acción autenticada en la plataforma, no solo
    // login. En producción implementar con middleware que actualice user.lastActivityAt en cada
    // request. Umbral: ALERT_CONFIG.EVALUATOR_LOGIN_DAYS días sin actividad con asignación activa.
    // Acta REP-013 define "no se han logueado"; esta implementación es más amplia (y correcta):
    // un evaluador que entregó una evaluación ayer no debe disparar esta alerta.
    description: 'El evaluador tiene asignación activa pero no ha tenido actividad en la plataforma en los últimos días.',
    visibleParaRol: ['admin'],
    anchor: 'evaluator',
    defaultPriority: AlertPriority.MEDIUM,
  },
  {
    type: AlertType.EVALUATOR_OVERLOADED,
    label: 'Evaluador Sobrecargado',
    description: 'El evaluador supera el umbral de asignaciones activas.',
    visibleParaRol: ['admin'],
    anchor: 'evaluator',
    defaultPriority: AlertPriority.LOW, // escala a MEDIUM — ver Fase 3
  },
];

export function getCatalogForRole(role: AlertRole): AlertCatalogEntry[] {
  return ALERT_CATALOG.filter((e) => e.visibleParaRol.includes(role));
}

export function getAlertLabel(type: AlertType): string {
  return ALERT_CATALOG.find((e) => e.type === type)?.label ?? type;
}

export function getAlertAnchor(type: AlertType): AlertAnchor {
  return ALERT_CATALOG.find((e) => e.type === type)?.anchor ?? 'application';
}
